import { useState, useRef, useEffect } from 'react'
import { FiPlay, FiPause, FiVolume2, FiVolumeX, FiMaximize, FiMinimize, FiSkipForward, FiSkipBack } from 'react-icons/fi'
import './CustomVideoPlayer.css'

const CustomVideoPlayer = ({ url, watermark, onProgress, onEnded, initialTime = 0 }) => {
  const videoRef = useRef(null)
  const containerRef = useRef(null)
  const progressBarRef = useRef(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [volume, setVolume] = useState(1)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [showControls, setShowControls] = useState(true)
  const [isHovering, setIsHovering] = useState(false)
  const controlsTimeoutRef = useRef(null)
  const [showScreenshotWarning, setShowScreenshotWarning] = useState(false)
  const [isSeeking, setIsSeeking] = useState(false)
  const isSeekingRef = useRef(false)

  // Download and screenshot protection
  useEffect(() => {
    const video = videoRef.current
    const container = containerRef.current
    if (!video || !container) return

    // Detect print dialog
    const handleBeforePrint = () => {
      if (container) {
        container.style.filter = 'blur(20px)'
        setShowScreenshotWarning(true)
      }
    }

    const handleAfterPrint = () => {
      if (container) {
        container.style.filter = ''
        setShowScreenshotWarning(false)
      }
    }

    // Detect print media query
    const printMediaQuery = window.matchMedia('print')
    const handlePrintChange = (mq) => {
      if (mq.matches) {
        handleBeforePrint()
      } else {
        handleAfterPrint()
      }
    }
    printMediaQuery.addEventListener('change', handlePrintChange)

    // Block print dialog
    window.addEventListener('beforeprint', handleBeforePrint)
    window.addEventListener('afterprint', handleAfterPrint)

    // Disable right-click context menu
    const handleContextMenu = (e) => {
      e.preventDefault()
      return false
    }

    // Disable keyboard shortcuts (Ctrl+S, Ctrl+P, Print Screen, etc.)
    const handleKeyDown = (e) => {
      // Block Ctrl+S (Save), Ctrl+P (Print), Ctrl+Shift+I (DevTools), F12
      if (
        (e.ctrlKey && (e.key === 's' || e.key === 'S' || e.key === 'p' || e.key === 'P')) ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'i')) ||
        e.key === 'F12' ||
        e.keyCode === 44 || // Print Screen key code
        e.key === 'PrintScreen' || 
        e.key === 'Print' ||
        (e.key === 'F13') // Some systems use F13 for Print Screen
      ) {
        e.preventDefault()
        e.stopPropagation()
        return false
      }
    }

    // Additional Print Screen detection
    const handleKeyUp = (e) => {
      if (e.keyCode === 44 || e.key === 'PrintScreen' || e.key === 'Print') {
        e.preventDefault()
        e.stopPropagation()
        // Show warning and blur content briefly
        if (container) {
          container.style.filter = 'blur(10px)'
          setShowScreenshotWarning(true)
          setTimeout(() => {
            if (container) container.style.filter = ''
            setShowScreenshotWarning(false)
          }, 2000)
        }
        return false
      }
    }

    // Detect Alt+PrintScreen (Windows) or Shift+Command+3/4 (Mac)
    const handleKeyPress = (e) => {
      if (
        (e.altKey && e.keyCode === 44) ||
        (e.shiftKey && e.metaKey && (e.keyCode === 51 || e.keyCode === 52))
      ) {
        e.preventDefault()
        e.stopPropagation()
        if (container) {
          container.style.filter = 'blur(10px)'
          setShowScreenshotWarning(true)
          setTimeout(() => {
            if (container) container.style.filter = ''
            setShowScreenshotWarning(false)
          }, 2000)
        }
        return false
      }
    }

    // Disable drag and drop
    const handleDragStart = (e) => {
      e.preventDefault()
      return false
    }

    // Disable text selection
    const handleSelectStart = (e) => {
      e.preventDefault()
      return false
    }

    // Add event listeners
    video.addEventListener('contextmenu', handleContextMenu)
    container.addEventListener('contextmenu', handleContextMenu)
    document.addEventListener('keydown', handleKeyDown, true) // Use capture phase
    document.addEventListener('keyup', handleKeyUp, true) // Use capture phase
    document.addEventListener('keypress', handleKeyPress, true)
    window.addEventListener('keydown', handleKeyDown, true)
    window.addEventListener('keyup', handleKeyUp, true)
    window.addEventListener('keypress', handleKeyPress, true)
    video.addEventListener('dragstart', handleDragStart)
    container.addEventListener('dragstart', handleDragStart)
    video.addEventListener('selectstart', handleSelectStart)
    container.addEventListener('selectstart', handleSelectStart)

    // CSS protection - disable user selection
    video.style.userSelect = 'none'
    video.style.webkitUserSelect = 'none'
    video.style.MozUserSelect = 'none'
    video.style.msUserSelect = 'none'
    video.style.webkitTouchCallout = 'none'

    return () => {
      printMediaQuery.removeEventListener('change', handlePrintChange)
      window.removeEventListener('beforeprint', handleBeforePrint)
      window.removeEventListener('afterprint', handleAfterPrint)
      video.removeEventListener('contextmenu', handleContextMenu)
      container.removeEventListener('contextmenu', handleContextMenu)
      document.removeEventListener('keydown', handleKeyDown, true)
      document.removeEventListener('keyup', handleKeyUp, true)
      document.removeEventListener('keypress', handleKeyPress, true)
      window.removeEventListener('keydown', handleKeyDown, true)
      window.removeEventListener('keyup', handleKeyUp, true)
      window.removeEventListener('keypress', handleKeyPress, true)
      video.removeEventListener('dragstart', handleDragStart)
      container.removeEventListener('dragstart', handleDragStart)
      video.removeEventListener('selectstart', handleSelectStart)
      container.removeEventListener('selectstart', handleSelectStart)
    }
  }, [])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const updateTime = () => {
      // Don't update state during seeking to prevent conflicts
      if (!isSeekingRef.current) {
        setCurrentTime(video.currentTime)
        // Call onProgress callback for progress tracking (use timeupdate, not progress event)
        if (onProgress && video.duration && video.duration > 0) {
          onProgress(video.currentTime / video.duration, video.duration)
        }
      }
    }
    const updateDuration = () => {
      if (video.duration && !isNaN(video.duration)) {
        setDuration(video.duration)
        // Resume from saved position if initialTime is provided
        if (initialTime > 0 && initialTime < video.duration) {
          video.currentTime = initialTime
          setCurrentTime(initialTime)
        }
      }
    }
    const handleEnded = () => {
      setIsPlaying(false)
      if (onEnded) onEnded()
    }
    const handleProgress = () => {
      // This is for buffering progress, not playback progress
      // Playback progress is handled in updateTime via timeupdate event
    }

    video.addEventListener('timeupdate', updateTime)
    video.addEventListener('loadedmetadata', updateDuration)
    video.addEventListener('durationchange', updateDuration)
    video.addEventListener('ended', handleEnded)
    video.addEventListener('progress', handleProgress)

    return () => {
      video.removeEventListener('timeupdate', updateTime)
      video.removeEventListener('loadedmetadata', updateDuration)
      video.removeEventListener('durationchange', updateDuration)
      video.removeEventListener('ended', handleEnded)
      video.removeEventListener('progress', handleProgress)
    }
  }, [onProgress, onEnded, initialTime])

  useEffect(() => {
    if (isPlaying) {
      videoRef.current?.play()
    } else {
      videoRef.current?.pause()
    }
  }, [isPlaying])

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = volume
      videoRef.current.muted = isMuted
    }
  }, [volume, isMuted])

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  // Handle URL changes - reset video state when URL changes
  useEffect(() => {
    const video = videoRef.current
    if (!video || !url) return

    // Reset video state when URL changes
    video.pause()
    setCurrentTime(0)
    setDuration(0)
    setIsPlaying(false)
    
    // Load the new source
    video.load()
    
    // Resume from initialTime if provided
    if (initialTime > 0) {
      const handleLoadedMetadata = () => {
        if (video.duration && initialTime < video.duration) {
          video.currentTime = initialTime
          setCurrentTime(initialTime)
        }
        video.removeEventListener('loadedmetadata', handleLoadedMetadata)
      }
      video.addEventListener('loadedmetadata', handleLoadedMetadata)
    }
  }, [url, initialTime])

  const togglePlay = () => {
    setIsPlaying(!isPlaying)
  }

  const toggleMute = () => {
    setIsMuted(!isMuted)
  }

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      containerRef.current?.requestFullscreen()
    } else {
      document.exitFullscreen()
    }
  }

  const handleSeek = (e) => {
    e.preventDefault()
    e.stopPropagation()
    
    const video = videoRef.current
    const progressBar = progressBarRef.current
    if (!video || !progressBar || !duration || duration === 0 || isNaN(duration)) return
    
    // Use ref for more accurate bounding rect
    const rect = progressBar.getBoundingClientRect()
    
    // Calculate position relative to the progress bar container
    const x = e.clientX - rect.left
    const width = rect.width
    
    // Ensure we have a valid width
    if (width <= 0) return
    
    // Check if element or document has RTL direction
    const computedStyle = window.getComputedStyle(progressBar)
    const docDirection = window.getComputedStyle(document.documentElement).direction
    const isRTL = computedStyle.direction === 'rtl' || docDirection === 'rtl'
    
    // Calculate position (0 to 1), clamp to valid range
    let pos = Math.max(0, Math.min(1, x / width))
    
    // If RTL, reverse the calculation: position from right = 1 - position from left
    // OR if the seek is currently reversed, reverse it to fix it
    // Try reversing if it feels backwards
    if (isRTL) {
      pos = 1 - pos
    }
    
    // Calculate new time based on position
    const newTime = pos * duration
    
    // Ensure newTime is valid and within bounds
    if (isNaN(newTime) || newTime < 0 || newTime > duration) return
    
    // Set seeking flag to prevent timeupdate from overriding our seek
    isSeekingRef.current = true
    setIsSeeking(true)
    
    // Update video time
    try {
      video.currentTime = newTime
      setCurrentTime(newTime)
    } catch (error) {
      console.error('Error seeking video:', error)
    }
    
    // Allow timeupdate to resume after a brief delay
    setTimeout(() => {
      isSeekingRef.current = false
      setIsSeeking(false)
    }, 150)
  }
  
  const handleSeekMouseMove = (e) => {
    // Handle dragging on the progress bar
    if (e.buttons === 1) { // Left mouse button is pressed
      e.preventDefault()
      e.stopPropagation()
      handleSeek(e)
    }
  }

  const skip = (seconds) => {
    if (videoRef.current) {
      videoRef.current.currentTime += seconds
    }
  }

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const handleMouseMove = () => {
    setIsHovering(true)
    setShowControls(true)
    clearTimeout(controlsTimeoutRef.current)
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) {
        setShowControls(false)
      }
    }, 3000)
  }

  const handleMouseLeave = () => {
    setIsHovering(false)
    if (isPlaying) {
      setShowControls(false)
    }
  }

  // Calculate progress percentage for visual display
  const progress = duration ? (currentTime / duration) * 100 : 0
  // Ensure progress is between 0 and 100
  const clampedProgress = Math.max(0, Math.min(100, progress))

  return (
    <div
      ref={containerRef}
      className="custom-video-player"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <video
        ref={videoRef}
        src={url}
        className="video-element"
        playsInline
        onClick={togglePlay}
        controlsList="nodownload nofullscreen noremoteplayback"
        disablePictureInPicture
        onContextMenu={(e) => e.preventDefault()}
        onError={(e) => {
          // Handle video loading errors gracefully
          const video = e.target
          if (video.error) {
            console.error('Video loading error:', video.error)
          }
        }}
        style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
      />
      
      {watermark && (
        <div className="video-watermark">
          <span>{watermark}</span>
        </div>
      )}

      {/* Logo watermark at bottom right */}
      <div className="video-logo-watermark">
        <img 
          src="/logo.png" 
          alt="Logo" 
          draggable="false"
          onError={(e) => {
            // Hide logo if it fails to load (404)
            e.target.style.display = 'none'
          }}
        />
      </div>

      {/* Additional security overlay - makes screenshots less useful */}
      <div className="security-overlay">
        <div className="security-pattern"></div>
      </div>

      {showScreenshotWarning && (
        <div className="screenshot-warning">
          <div className="warning-content">
            <h3>⚠️ Screenshots are not allowed</h3>
            <p>This content is protected</p>
          </div>
        </div>
      )}

      <div className={`video-controls ${showControls || isHovering ? 'visible' : ''}`}>
        <div 
          ref={progressBarRef}
          className="progress-bar-container" 
          onClick={handleSeek}
          onMouseMove={handleSeekMouseMove}
          onMouseDown={(e) => {
            // Start seeking on mousedown
            if (e.button === 0) { // Left mouse button
              handleSeek(e)
            }
          }}
        >
          <div className="progress-bar">
            <div className="progress-filled" style={{ width: `${clampedProgress}%` }} />
            <div className="progress-handle" style={{ left: `${clampedProgress}%` }} />
          </div>
        </div>

        <div className="controls-bottom">
          <div className="controls-left">
            <button className="control-btn" onClick={togglePlay}>
              {isPlaying ? <FiPause /> : <FiPlay />}
            </button>
            <button className="control-btn" onClick={() => skip(-10)}>
              <FiSkipBack />
            </button>
            <button className="control-btn" onClick={() => skip(10)}>
              <FiSkipForward />
            </button>
            <div className="volume-control">
              <button className="control-btn" onClick={toggleMute}>
                {isMuted || volume === 0 ? <FiVolumeX /> : <FiVolume2 />}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={isMuted ? 0 : volume}
                onChange={(e) => {
                  setVolume(parseFloat(e.target.value))
                  setIsMuted(false)
                }}
                className="volume-slider"
              />
            </div>
            <div className="time-display">
              <span>{formatTime(currentTime)}</span>
              <span>/</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          <div className="controls-right">
            <button className="control-btn" onClick={toggleFullscreen}>
              {isFullscreen ? <FiMinimize /> : <FiMaximize />}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CustomVideoPlayer

